import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { mobileCodeLogin, sendVerifyCode } from '@/services/account';
import type { PortalUser } from '@/services/request';

interface LoginModalProps {
  open: boolean;
  onClose: () => void;
  onSuccess: (user: PortalUser) => void;
  /** 外部提示（如登录已失效），显示在表单上方 */
  notice?: string;
}

const PHONE_RE = /^1[3-9]\d{9}$/;
const CODE_RE = /^\d{6}$/;

export default function LoginModal({ open, onClose, onSuccess, notice }: LoginModalProps) {
  const [phone, setPhone] = useState('');
  const [code, setCode] = useState('');
  const [agreed, setAgreed] = useState(true);
  const [countdown, setCountdown] = useState(0);
  const [sending, setSending] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [codeTip, setCodeTip] = useState('');
  const [error, setError] = useState('');
  const timerRef = useRef<number | null>(null);

  useEffect(() => {
    if (countdown <= 0) return undefined;
    timerRef.current = window.setTimeout(() => setCountdown((v) => v - 1), 1000);
    return () => {
      if (timerRef.current) window.clearTimeout(timerRef.current);
    };
  }, [countdown]);

  useEffect(() => {
    if (!open) {
      setError('');
      setCode('');
      setCountdown(0);
      setCodeTip('');
      setSending(false);
      setSubmitting(false);
    }
  }, [open]);

  useEffect(() => {
    if (!open) return undefined;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [open, onClose]);

  if (!open) return null;

  const handleSendCode = async () => {
    if (!PHONE_RE.test(phone)) {
      setError('请输入正确的手机号码');
      return;
    }
    if (sending || countdown > 0) return;
    setError('');
    setCodeTip('');
    setSending(true);
    try {
      await sendVerifyCode(phone);
      setCountdown(60);
      // 已接入真实短信服务，验证码通过短信发送，不在页面回显
      setCodeTip('验证码已发送，请注意查收短信');
    } catch (err) {
      setError(err instanceof Error ? err.message : '验证码发送失败，请稍后重试');
    } finally {
      setSending(false);
    }
  };

  const handleSubmit = async () => {
    if (!PHONE_RE.test(phone)) {
      setError('请输入正确的手机号码');
      return;
    }
    if (!CODE_RE.test(code)) {
      setError('请输入 6 位数字验证码');
      return;
    }
    if (!agreed) {
      setError('请先阅读并同意用户协议与隐私政策');
      return;
    }
    if (submitting) return;
    setError('');
    setSubmitting(true);
    try {
      const result = await mobileCodeLogin(phone, code);
      onSuccess({ token: result.token, uId: result.uId, userName: result.userName, mobile: phone });
    } catch (err) {
      setError(err instanceof Error ? err.message : '登录失败，请稍后重试');
    } finally {
      setSubmitting(false);
    }
  };

  return createPortal(
    <div
      className="fixed inset-0 z-[110] flex items-center justify-center px-4 animate-modal-fade bg-foreground-950/60 backdrop-blur-sm"
      onClick={onClose}
    >
      <div
        className="w-full max-w-md rounded-lg border border-background-200 bg-background-50 p-6 md:p-8 animate-modal-pop"
        onClick={(event) => event.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-label="会员登录"
      >
        <div className="flex items-start justify-between mb-6">
          <div>
            <div className="flex items-center gap-2 mb-2">
              <span className="w-1.5 h-4 rounded-full bg-primary-500"></span>
              <span className="text-sm font-medium text-primary-600">会员中心</span>
            </div>
            <h3 className="font-heading text-xl font-bold text-foreground-900">手机号登录</h3>
            <p className="mt-1 text-xs text-foreground-500">未注册的手机号将自动创建会员账号</p>
          </div>
          <button
            type="button"
            aria-label="关闭"
            onClick={onClose}
            className="w-9 h-9 flex items-center justify-center rounded-md text-foreground-500 hover:bg-background-100 transition-colors cursor-pointer"
          >
            <i className="ri-close-line text-xl"></i>
          </button>
        </div>

        <label className="block mb-2 text-sm text-foreground-700" htmlFor="login-phone">
          手机号
        </label>
        <div className="flex items-center h-12 mb-4 overflow-hidden rounded-md border border-background-300 bg-background-50 focus-within:border-primary-400 transition-colors">
          <span className="flex items-center h-full px-3 text-sm border-r text-foreground-600 border-background-200">
            +86
          </span>
          <input
            id="login-phone"
            type="tel"
            inputMode="numeric"
            autoComplete="off"
            maxLength={11}
            value={phone}
            onChange={(event) => setPhone(event.target.value.replace(/\D/g, ''))}
            placeholder="请输入手机号码"
            className="flex-1 h-full px-3 text-sm bg-transparent outline-none text-foreground-900 placeholder:text-foreground-400"
          />
        </div>

        <label className="block mb-2 text-sm text-foreground-700" htmlFor="login-code">
          验证码
        </label>
        <div className="flex items-center gap-3 mb-4">
          <div className="flex-1 flex items-center h-12 rounded-md border border-background-300 bg-background-50 focus-within:border-primary-400 transition-colors">
            <input
              id="login-code"
              type="text"
              inputMode="numeric"
              autoComplete="off"
              maxLength={6}
              value={code}
              onChange={(event) => setCode(event.target.value.replace(/\D/g, ''))}
              placeholder="请输入验证码"
              className="flex-1 h-full px-3 text-sm bg-transparent outline-none text-foreground-900 placeholder:text-foreground-400"
            />
          </div>
          <button
            type="button"
            onClick={handleSendCode}
            disabled={countdown > 0 || sending}
            className="h-12 px-4 text-sm whitespace-nowrap rounded-md border border-primary-300 text-primary-600 hover:bg-primary-50 transition-colors cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {sending ? '发送中...' : countdown > 0 ? `${countdown}s 后重发` : '获取验证码'}
          </button>
        </div>

        <label className="flex items-start gap-2 mb-4 cursor-pointer select-none">
          <input
            type="checkbox"
            checked={agreed}
            onChange={(event) => setAgreed(event.target.checked)}
            className="mt-0.5 w-4 h-4 cursor-pointer accent-primary-500"
          />
          <span className="text-xs leading-relaxed text-foreground-500">
            我已阅读并同意
            <a href="#contact" className="text-primary-600 hover:underline">《用户协议》</a>
            与
            <a href="#contact" className="text-primary-600 hover:underline">《隐私政策》</a>
          </span>
        </label>

        {notice && (
          <p className="flex items-center gap-1 mb-3 text-xs text-secondary-700">
            <i className="ri-information-line"></i>
            {notice}
          </p>
        )}

        {codeTip && !error && (
          <p className="flex items-center gap-1 mb-3 text-xs text-accent-700">
            <i className="ri-checkbox-circle-line"></i>
            {codeTip}
          </p>
        )}

        {error && (
          <p className="flex items-center gap-1 mb-3 text-xs text-primary-600">
            <i className="ri-error-warning-line"></i>
            {error}
          </p>
        )}

        <button
          type="button"
          onClick={handleSubmit}
          disabled={submitting}
          className="w-full h-12 text-sm font-medium transition-colors rounded-md bg-primary-500 hover:bg-primary-600 disabled:opacity-60 disabled:cursor-not-allowed text-background-50 cursor-pointer whitespace-nowrap"
        >
          {submitting ? (
            <span className="inline-flex items-center gap-2">
              <i className="ri-loader-4-line animate-spin"></i>
              登录中...
            </span>
          ) : (
            '登录 / 注册'
          )}
        </button>

        <p className="mt-4 text-xs text-center text-foreground-400">
          登录即代表同意平台使用您的手机号进行账号注册与安全校验
        </p>
      </div>
    </div>,
    document.body,
  );
}